import { SpeedGame, WINDS, ONLINE_SPEED, handTiles, meldTiles, callOptions, tileName, tileLabel, roundName, yakuName } from '../game.js';
import { OnlineGame, OnlineClient } from './online.js';
import { setupDisplay } from './display.js';
import { createTileLoader, tileSrc, renderButtons, renderRiver } from './tiles.js';

const $id = (id) => document.getElementById(id);
const local = new SpeedGame();
let game = local;
let selecting = false; // choosing two hand tiles for a call reservation
let picked = [];
let pickedHand = '';
let pickHint = '';
let sound = false;
let audio = null;
let toastTimer = null;
let shownResult = null;
let page = 'home';
let onlineMode = 'random';
let pausedByDialog = false;
let reopenSettings = false;
const tiles = createTileLoader({ onProgress: updateTileLoading });
const fmt = (n) => n.toLocaleString('en-US');
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const htmlCache = new WeakMap();

function setHTML(id, html) {
  const el = $id(id);
  if (htmlCache.get(el) !== html) {
    el.innerHTML = html;
    htmlCache.set(el, html);
  }
}

function tileHTML(t, cls = '', attrs = '') {
  return `<span class="tile ${cls}" ${attrs} data-t="${t}" data-label="${tileLabel(t)}" title="${tileName(t)}"><img src="${tileSrc(t)}" alt="${tileName(t)}" draggable="false"></span>`;
}

function meldHTML(m) {
  return `<span class="meld">${meldTiles(m).map((t) => tileHTML(t)).join('')}</span>`;
}

function updateTileLoading() {
  const { status, loaded, total } = tiles.state;
  const ready = status === 'ready';
  for (const b of document.querySelectorAll('[data-action="start"],[data-action="online-create"],[data-action="online-join"],[data-action="online-match"]')) b.disabled = !ready;
  for (const el of document.querySelectorAll('[data-tile-loading]')) {
    el.hidden = ready;
    el.querySelector('span').textContent = status === 'error' ? "Couldn't load the tile images." : `Loading tiles… ${loaded}/${total}`;
    el.querySelector('button').hidden = status !== 'error';
  }
}

function toast(msg) {
  $id('toast').textContent = msg;
  $id('toast').classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => $id('toast').classList.remove('show'), 2300);
}

function showPage(next) {
  if (page === next && document.documentElement.dataset.page === next) return;
  const changed = page !== next;
  page = next;
  document.documentElement.dataset.page = page;
  document.documentElement.classList.toggle('in-game', page === 'game');
  $id('home-screen').hidden = page !== 'home';
  $id('game-screen').hidden = page !== 'game';
  $id('home-brand').hidden = page !== 'home';
  $id('game-back').hidden = page !== 'game';
  $id('mode-label').hidden = page !== 'game';
  $id('cpu-home-description').textContent =
    local.status === 'paused' ? 'Resume your paused match' : local.status === 'ended' ? 'See the result and continue' : 'Play solo';
  if (changed) {
    $id('play-shell').scrollTop = 0;
    window.scrollTo?.({ top: 0, left: 0, behavior: 'instant' });
    (page === 'game' ? $id('game-back') : document.querySelector('[data-action="home-cpu"]')).focus({ preventScroll: true });
  }
  window.dispatchEvent(new window.Event('mahjong-view-change'));
}

function openSettings() {
  $id('speed-error').textContent = '';
  if (!game.online && page === 'game' && game.status === 'playing') {
    pausedByDialog = true;
    game.pause();
  }
  $id('settings-dialog').showModal();
}

function openRules() {
  reopenSettings = $id('settings-dialog').open;
  if (!game.online && page === 'game' && game.status === 'playing') {
    pausedByDialog = true;
    game.pause();
  }
  $id('rules-dialog').showModal();
  if (reopenSettings) $id('settings-dialog').close();
}

function closeRules() {
  if (reopenSettings) {
    reopenSettings = false;
    $id('settings-dialog').showModal();
  }
  $id('rules-dialog').close();
}

for (const id of ['settings-dialog', 'rules-dialog']) {
  $id(id).addEventListener('close', () => {
    if (!pausedByDialog || $id('settings-dialog').open || $id('rules-dialog').open) return;
    pausedByDialog = false;
    if (page === 'game' && !game.online && game.status === 'paused') game.resume();
  });
}
$id('rules-dialog').addEventListener('cancel', (e) => {
  e.preventDefault();
  closeRules();
});

function openOnline(mode) {
  onlineMode = mode;
  $id('online-error').textContent = '';
  renderOnline();
  if (!$id('online-dialog').open) $id('online-dialog').showModal();
}

function goBack() {
  if (resultLocked()) return extendMash();
  if (game.online) return $id('leave-dialog').showModal();
  stopSelecting();
  if (game.status === 'playing') game.pause();
  $id('result-dialog').close();
  showPage('home');
}

function beep(kind) {
  if (!sound) return;
  try {
    audio ??= new (window.AudioContext || window.webkitAudioContext)();
    if (audio.state === 'suspended') audio.resume();
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.connect(gain);
    gain.connect(audio.destination);
    const t = audio.currentTime;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(kind === 'win' ? 740 : kind === 'call' ? 580 : 350, t);
    osc.frequency.exponentialRampToValueAtTime(kind === 'win' ? 980 : 210, t + 0.12);
    gain.gain.setValueAtTime(0.06, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.13);
    osc.start(t);
    osc.stop(t + 0.14);
  } catch {
    sound = false;
    $id('sound').textContent = 'Sound off';
  }
}

function renderOpponent(i) {
  const p = game.players[i];
  const seat = game.seat(i);
  $id(`player-${i}`).classList.toggle('in-riichi', p.hand.lizhi);
  setHTML(
    `player-${i}`,
    `<div class="player-main"><span class="seat ${seat === 0 ? 'dealer' : ''}" title="${seat === 0 ? 'Dealer, ' : ''}seat wind">${WINDS[seat]}</span><span class="player-name">${esc(p.name)}</span></div><div class="opponent-score-line"><strong class="player-score">${fmt(game.scores[i])}</strong><span class="cpu-state ${p.hand.lizhi ? 'reach' : ''}">${p.hand.lizhi ? 'Riichi' : p.hand._zimo ? 'Discarding' : 'Waiting'}</span></div>${p.hand._fulou.length ? `<div class="small-melds">${p.hand._fulou.map(meldHTML).join('')}</div>` : ''}`,
  );
}

function renderRiverOf(i) {
  renderRiver($id(`river-${i}`), game.players[i].river, game.lastDiscard?.player === i);
}

function renderClock() {
  const c = game.actionClock();
  $id('action-timer').hidden = !c;
  $id('action-time-track').hidden = !c;
  if (!c) return;
  $id('action-timer-label').textContent = c.running ? `${c.automatic ? 'Auto ' : ''}${c.kind === 'draw' ? 'draw in' : 'discard in'}` : 'Paused';
  $id('action-timer-seconds').textContent = (Math.ceil(c.remainingMs / 100) / 10).toFixed(1);
  const urgent = !c.automatic && c.remainingMs <= 2000 && c.running;
  $id('action-timer').classList.toggle('urgent', urgent);
  $id('action-time-track').classList.toggle('urgent', urgent);
  $id('action-time-fill').style.width = `${(c.remainingMs / c.limitMs) * 100}%`;
  $id('action-timer').setAttribute('aria-label', `Auto ${c.kind === 'draw' ? 'draw' : 'discard'} in ${(c.remainingMs / 1000).toFixed(1)} seconds${c.running ? '' : ', paused'}`);
}

let handKey = '';

function stopSelecting() {
  if (selecting && game.online) game.holdSelection(false).catch(() => {});
  selecting = false;
  picked = [];
  pickedHand = '';
  pickHint = '';
  if (!game.online && game.autoDraw && !game.players[0].hand.lizhi) game.players[0].next = game.elapsed + 900;
}

function toggleSelecting() {
  if (selecting) {
    stopSelecting();
    render();
    return;
  }
  if (!['ready', 'playing', 'paused'].includes(game.status) || game.players[0].hand.lizhi) return;
  selecting = true;
  picked = [];
  pickHint = '';
  pickedHand = game.players[0].hand.toString();
  game.riichiArmed = false;
  if (game.online) game.holdSelection(true).catch((e) => toast(e.message));
  render();
}

async function pickTile(index, handString) {
  if (!selecting || !Number.isInteger(index)) return;
  const hand = game.players[0].hand;
  if (handString !== hand.toString() || pickedHand !== hand.toString()) {
    picked = [];
    pickedHand = hand.toString();
    pickHint = 'Your hand changed. Pick the two tiles again.';
    render();
    return;
  }
  const list = handTiles(hand);
  if (index < 0 || index >= list.length) return;
  if (picked.includes(index)) {
    picked = picked.filter((i) => i !== index);
    pickHint = '';
    render();
    return;
  }
  picked.push(index);
  pickHint = '';
  if (picked.length === 2) {
    const pair = picked.map((i) => list[i]).sort().join(',');
    const matches = callOptions(hand).filter(
      (o) => (o.meld.match(/\d(?![+=-])/g) || []).map((d) => o.meld[0] + d).sort().join(',') === pair,
    );
    if (!matches.length) {
      picked.pop();
      pickHint = "Those two tiles can't make a call. Pick a different second tile.";
      render();
      return;
    }
    const fresh = matches.filter((o) => !game.reservations.some((r) => r.key === o.key));
    if (game.online) {
      if (game.pending) return;
      await game.setReservations([...game.reservations, ...fresh].map((o) => o.key));
    } else game.reservations.push(...fresh);
    const msg = `${matches[0].type === 'pon' ? 'Pon' : 'Chi'} on ${matches.map((o) => tileName(o.tile)).join(' / ')} ${fresh.length ? 'reserved' : 'was already reserved'}`;
    stopSelecting();
    game.notify('reservation', msg);
    toast(msg);
    return;
  }
  render();
}

function checkSelection() {
  if (!selecting) return;
  const hand = game.players[0].hand;
  if (hand.lizhi || game.status === 'ended') {
    stopSelecting();
    return;
  }
  if (pickedHand !== hand.toString()) {
    picked = [];
    pickedHand = hand.toString();
    pickHint = 'Your hand changed. Pick the two tiles again.';
  }
}

function renderHand() {
  const me = game.players[0];
  const hand = me.hand;
  const handString = hand.toString();
  const key = JSON.stringify([handString, game.status, game.riichiArmed, game.wall.length >= 4, game.wall.length > 0, game.scores[0] >= 1000, game.pending, selecting, picked, pickHint, me.draws, me.river.length]);
  if (key === handKey) return;
  handKey = key;
  const legal = hand.get_dapai() || [];
  const riichiOk = game.riichiDiscards();
  const list = handTiles(hand);
  const canDiscard = game.status === 'playing' && !!hand._zimo && !game.pending && !hand.lizhi;
  const seen = new Map();
  renderButtons(
    $id('hand'),
    list.map((t, i) => {
      const drawn = hand._zimo?.length === 2 && i === list.length - 1;
      const action = drawn ? legal.find((x) => x === t + '_') || legal.find((x) => x.slice(0, 2) === t) : legal.find((x) => x === t) || legal.find((x) => x.slice(0, 2) === t);
      const allowed = !!action && (!game.riichiArmed || riichiOk.includes(action));
      const isPicked = picked.includes(i);
      const n = seen.get(t) || 0;
      if (!drawn) seen.set(t, n + 1);
      return {
        key: drawn ? 'drawn' : `${t}:${n}`,
        tile: t,
        selected: isPicked,
        classes: `tile ${drawn ? 'drawn' : ''} ${!selecting && canDiscard && !allowed ? 'denied' : ''} ${game.riichiArmed && allowed ? 'riichi-ok' : ''} ${selecting ? 'selectable' : ''} ${isPicked ? 'reservation-selected' : ''}`,
        attributes: {
          'data-action': selecting ? 'select-reservation-tile' : 'discard',
          'data-index': i,
          'data-hand': handString,
          'data-tile': action || t,
          'aria-pressed': selecting ? String(isPicked) : null,
          disabled: !selecting && (!canDiscard || !allowed) ? '' : null,
          'aria-label': `${selecting ? (isPicked ? 'Unselect' : 'Select for call reservation:') : 'Discard'} ${tileName(t)}${drawn ? ' (drawn tile)' : ''}`,
        },
      };
    }),
  );
  setHTML('your-melds', hand._fulou.map(meldHTML).join(''));

  let status = 'Check your hand, then start the round';
  if (game.status === 'playing') {
    if (game.riichiArmed) status = '<strong>Riichi</strong> · discard one of the glowing tiles';
    else if (hand.lizhi) status = '<strong>In riichi</strong> · auto draw and discard every 0.5s';
    else if (hand._zimo) status = hand._zimo.length > 2 ? '<strong>Call made</strong> · discard one tile' : 'Tap a tile to <strong>discard</strong>';
    else if (game.waits(0).length) status = `<strong>Tenpai</strong>${game.furiten(0) ? ' · furiten (tsumo only)' : ' · ron is automatic'}`;
    else status = '<strong>Ready to draw</strong> · or wait for a call';
  } else if (game.status === 'paused') status = 'Paused · everyone is stopped';
  else if (game.status === 'ended') status = 'This hand is over';
  if (selecting) status = `<strong>Call reservation</strong> · pick 2 tiles from your hand (${picked.length}/2)`;
  setHTML('hand-status', status);
  $id('draw-count').textContent = `Draws: ${me.draws}`;
  $id('draw').disabled = hand.lizhi || !!game.pending || selecting || game.status !== 'playing' || !!hand._zimo || !game.wall.length;
  $id('auto-draw').disabled = hand.lizhi;
  setHTML('draw', hand.lizhi ? '<span>Auto play</span><small>every 0.5s</small>' : game.wall.length ? '<span>Draw</span><small>take one tile</small>' : '<span>Wall empty</span>');
  $id('riichi').disabled = !!game.pending || selecting || game.status !== 'playing' || (!game.riichiArmed && !riichiOk.length);
  $id('riichi').classList.toggle('armed', game.riichiArmed);
  setHTML('riichi', game.riichiArmed ? 'Cancel riichi' : 'Riichi<span>1,000 pts</span>');
  $id('reserve-hand').disabled = !!game.pending || hand.lizhi || game.status === 'ended';
  $id('reserve-hand').classList.toggle('armed', selecting);
  $id('reserve-hand').setAttribute('aria-pressed', String(selecting));
  setHTML('reserve-hand', selecting ? '<span>Stop picking</span><small>back to discarding</small>' : '<span>Reserve call</span><small>pick 2 tiles</small>');
  $id('hand').classList.toggle('selecting-reservation', selecting);
  $id('reservation-selection-hint').hidden = !selecting;
  $id('reservation-selection-hint').textContent =
    pickHint || (picked.length ? 'One more. Tap a picked tile again to unpick it.' : 'Two of a kind reserves a pon; two connected numbers reserve a chi. Open waits reserve both sides.');
}

function renderReservations() {
  const hand = game.players[0].hand;
  $id('reservation-count').textContent = game.reservations.length;
  $id('clear-reservations').disabled = !game.reservations.length;
  const holding = !!hand._zimo;
  $id('reservation-state').classList.toggle('holding', holding);
  $id('reservation-state').textContent = hand.lizhi
    ? 'In riichi'
    : selecting
      ? 'The 5s clock keeps running'
      : holding
        ? 'Active after you discard'
        : game.status === 'ready'
          ? 'You can reserve before the start'
          : game.status === 'ended'
            ? 'Hand over'
            : 'Watching for calls';
  $id('reserve-hand').querySelector('span').textContent = selecting ? 'Stop picking' : game.reservations.length ? `Reserve call (${game.reservations.length})` : 'Reserve call';
  setHTML(
    'active-reservations',
    game.reservations.length
      ? game.reservations
          .map(
            (r) =>
              `<div class="active-item"><span>${tileHTML(r.tile)}${r.type === 'pon' ? 'Pon' : 'Chi'}${r.type === 'chi' ? ` ${r.meld.replace(/[^0-9]/g, '')}` : ''}</span><button data-action="reserve" data-key="${r.key}" aria-label="Cancel reservation on ${tileName(r.tile)}">×</button></div>`,
          )
          .join('')
      : '<span class="active-empty">Press “Reserve call”, then tap 2 tiles in your hand</span>',
  );
  setHTML('event-log', game.log.length ? game.log.slice(0, 12).map((e) => `<li>${esc(e.message)}</li>`).join('') : '<li>Match events show up here.</li>');
}

// The result screen ignores input for a moment so a mashed discard can't skip it.
let lockUntil = 0;
let mashUntil = 0;
let hiddenSince = null;
let inspected = null;
const heldPointers = new Set();
const heldKeys = new Set();
const resultShowing = () => game.status === 'ended' && game.result && game.result === shownResult;
const lockClock = () => hiddenSince ?? performance.now();

function resultLocked() {
  return !!resultShowing() && (lockClock() < Math.max(lockUntil, mashUntil) || heldPointers.size > 0 || heldKeys.size > 0);
}

function updateResultLock() {
  if (!resultShowing()) return;
  const locked = resultLocked();
  const wait = Math.max(0, lockUntil - lockClock());
  for (const b of $id('result-content').querySelectorAll('[data-result-action]')) b.disabled = locked;
  const hint = $id('result-confirm-hint');
  if (!hint) return;
  hint.textContent = wait > 0 ? `Check the scores · ${Math.ceil(wait / 1000)}s` : locked ? 'Stop tapping to continue' : game.round === 3 ? 'This screen stays until you close it.' : 'Continue when you are ready.';
  hint.classList.toggle('waiting', locked);
}

function extendMash() {
  mashUntil = lockClock() + 650;
  updateResultLock();
}

function openResult() {
  const now = performance.now();
  lockUntil = now + (game.round === 3 ? 5000 : 2000);
  mashUntil = 0;
  hiddenSince = document.hidden ? now : null;
  heldPointers.clear();
  heldKeys.clear();
  inspected = null;
  reopenSettings = false;
  for (const id of ['settings-dialog', 'rules-dialog', 'leave-dialog']) $id(id).close();
  $id('result-dialog').classList.toggle('final-result', game.round === 3);
  setHTML('result-content', resultHTML());
  updateResultLock();
  $id('result-dialog').showModal();
  $id('result-dialog').scrollTop = 0;
  $id('result-title').focus({ preventScroll: true });
}

document.addEventListener('pointerdown', (e) => {
  if ($id('result-dialog').open && resultLocked()) {
    heldPointers.add(e.pointerId);
    extendMash();
  }
}, true);
for (const type of ['pointerup', 'pointercancel']) {
  document.addEventListener(type, (e) => {
    if (heldPointers.delete(e.pointerId)) extendMash();
  }, true);
}
document.addEventListener('keydown', (e) => {
  if (!$id('result-dialog').open || !['Enter', 'Space', 'Escape'].includes(e.code)) return;
  if (resultLocked() || e.repeat) {
    heldKeys.add(e.code);
    extendMash();
    e.preventDefault();
  }
}, true);
document.addEventListener('keyup', (e) => {
  if (heldKeys.delete(e.code)) {
    extendMash();
    e.preventDefault();
  }
}, true);
$id('result-dialog').addEventListener('cancel', (e) => {
  if (resultLocked() || (resultShowing() && game.round === 3)) e.preventDefault();
  if (resultLocked()) extendMash();
});
document.addEventListener('visibilitychange', () => {
  if (!resultShowing()) return;
  const now = performance.now();
  if (document.hidden) {
    hiddenSince ??= now;
    heldPointers.clear();
    heldKeys.clear();
  } else if (hiddenSince !== null) {
    const away = now - hiddenSince;
    lockUntil += away;
    mashUntil += away;
    hiddenSince = null;
  }
  updateResultLock();
});

function yakuLabel(y) {
  if (typeof y.fanshu === 'number') return `${esc(yakuName(y.name))} ${y.fanshu} han`;
  return `${esc(yakuName(y.name))} ${y.fanshu.length > 1 ? `${y.fanshu.length}× yakuman` : 'yakuman'}`;
}

function resultHTML() {
  const r = game.result;
  const title = r.type === 'draw' ? 'Draw' : r.type === 'ron' ? 'Ron' : 'Tsumo';
  let summary = '';
  let yaku = '';
  let handRow = '';
  let points = '';
  if (r.type !== 'draw') {
    summary = `${esc(game.players[r.winner].name)} wins${r.from !== null ? ` · dealt in by ${esc(game.players[r.from].name)}` : ''}`;
    const list = handTiles(game.players[r.winner].hand);
    if (r.type === 'ron') list.push(r.tile);
    handRow = `<div class="result-tiles">${list.map((t, i) => tileHTML(t, i === list.length - 1 ? 'winning' : '')).join('')}${game.players[r.winner].hand._fulou.map(meldHTML).join('')}</div>`;
    yaku = `<div class="yaku-list">${r.score.hupai.map((y) => `<span>${yakuLabel(y)}</span>`).join('')}</div>`;
    const value = r.score.damanguan ? `${r.score.damanguan > 1 ? `${r.score.damanguan}× ` : ''}yakuman` : `${r.score.fu} fu ${r.score.fanshu} han`;
    points = `<div class="result-points">${fmt(r.score.defen)} <small>pts / ${value}</small></div>`;
    if (game.players[r.winner].hand.lizhi && game.dead[1]) yaku += `<div class="dora-line">Ura dora indicator ${tileHTML(game.dead[1])}</div>`;
  } else {
    summary = 'Every tile in the wall has been drawn and discarded.';
    yaku = `<p class="result-note">Tenpai: ${r.tenpai.map((t, i) => (t ? esc(game.players[i].name) : null)).filter(Boolean).join(', ') || 'nobody'}</p>`;
  }
  const final = game.round === 3;
  const scores = `<div class="result-scores"><div class="result-row result-column-labels" aria-hidden="true"><span>#</span><span>Player</span><span>This hand</span><span>Score</span></div>${game
    .ranking()
    .map(
      (i, n) =>
        `<div class="result-row ${i === 0 ? 'you' : ''}"><span>${n + 1}</span><span>${esc(game.players[i].name)}</span><span class="delta ${r.delta[i] > 0 ? 'positive' : r.delta[i] < 0 ? 'negative' : ''}">${r.delta[i] > 0 ? '+' : ''}${fmt(r.delta[i])}</span><strong>${fmt(game.scores[i])}</strong></div>`,
    )
    .join('')}</div>`;
  const pot = r.potAward ? `<p class="result-note">${esc(game.players[r.potAward.player].name)} collects the ${fmt(r.potAward.amount)} leftover riichi points.</p>` : '';
  const place = game.ranking().indexOf(0) + 1;
  const ordinal = ['1st', '2nd', '3rd', '4th'][place - 1];
  const hero = final
    ? `<div class="result-hero final-hero"><span class="eyebrow">FINAL RESULT · EAST ROUND COMPLETE</span><h2 id="result-title" tabindex="-1">Final result</h2><div class="final-placement">You finished <strong>${ordinal}</strong></div><div class="final-total"><span>Final score</span><strong>${fmt(game.scores[0])}<small> pts</small></strong></div></div>`
    : `<div class="result-hero"><span class="eyebrow">EAST ${game.round + 1} · HAND RESULT</span><h2 id="result-title" tabindex="-1">${title}</h2><p>${summary}</p>${points}</div>`;
  const details = final ? `<details class="final-hand-details"><summary>Last hand details · ${title}</summary><p class="result-note">${summary}</p>${points}${handRow}${yaku}</details>` : '';
  const primary = game.online
    ? final ? 'Done, back home' : 'Next hand starts automatically'
    : final ? 'Play again' : `Start East ${game.round + 2}`;
  const primaryAction = game.online ? (final ? 'online-leave' : 'inspect') : final ? 'new-match' : 'next-round';
  return `<button class="back-link dialog-back result-back" data-action="inspect" data-result-action disabled><span aria-hidden="true">←</span> Back</button>${hero}${final ? scores + pot + details : handRow + yaku + scores + pot}<p id="result-confirm-hint" class="result-confirm-hint waiting" role="status" aria-live="polite"></p><div class="result-buttons"><button class="inspect-button" data-action="inspect" data-result-action disabled>View the table</button><button class="primary" data-result-action disabled data-action="${primaryAction}">${primary}</button></div>`;
}

function render() {
  checkSelection();
  [1, 2, 3].forEach(renderOpponent);
  [0, 1, 2, 3].forEach(renderRiverOf);
  setHTML(
    'table-center',
    `<div class="round-label">${roundName(game.round)}<small>/ 4</small></div><div class="wall-info"><span>Wall</span><strong>${game.wall.length}</strong><span>left</span></div><div class="wall-meter"><div style="width:${(game.wall.length / 70) * 100}%"></div></div>${game.pot ? `<div class="pot-line">Riichi pot ${fmt(game.pot * 1000)}</div>` : ''}`,
  );
  setHTML('table-dora', `<span>Dora indicator</span>${tileHTML(game.dead[0])}`);
  setHTML(
    'self-score',
    `<span class="seat ${game.seat(0) === 0 ? 'dealer' : ''}">${WINDS[game.seat(0)]}</span><span>You</span><strong>${fmt(game.scores[0])}</strong>${game.players[0].hand.lizhi ? '<span class="cpu-state reach">Riichi</span>' : ''}`,
  );
  $id('live-light').classList.toggle('live', game.status === 'playing');
  $id('live-label').textContent = { ready: 'Not started', playing: 'In play', paused: 'Paused', ended: 'Hand over' }[game.status];
  $id('round-result').hidden = game.status !== 'ended';
  $id('round-result').textContent = game.round === 3 ? 'Final result' : 'Result / next';
  $id('pause').hidden = !!game.online || game.status === 'ended';
  $id('pause').disabled = !['playing', 'paused'].includes(game.status);
  $id('pause').textContent = game.status === 'paused' ? 'Resume' : 'Pause';
  const overlay = $id('table-overlay');
  overlay.hidden = game.status === 'playing' || (game.status === 'ended' && inspected === game.result);
  if (game.status === 'ready' && !game.online) {
    setHTML(
      'table-overlay',
      `<div class="start-card"><span class="eyebrow">EAST ${game.round + 1} · CPU MATCH</span><h2>Mahjong without waiting your turn.</h2><p>Draw and discard at your own pace.<br>Calls are reserved, wins are automatic.</p><p class="tile-loading" data-tile-loading><span role="status"></span><button class="text-button" data-action="retry-tiles" hidden>Retry</button></p><button class="primary" data-action="start">Start</button></div>`,
    );
  }
  if (game.status === 'paused') {
    setHTML('table-overlay', '<div class="start-card"><span class="eyebrow">PAUSED</span><h2>Taking a break.</h2><p>The CPUs are paused too.</p><button class="primary" data-action="pause">Resume</button></div>');
  }
  if (game.status === 'ended') {
    setHTML(
      'table-overlay',
      `<div class="start-card"><span class="eyebrow">${game.round === 3 ? 'MATCH COMPLETE' : 'HAND COMPLETE'}</span><h2>${game.round === 3 ? 'The East round is over.' : 'This hand is over.'}</h2><button class="primary" data-action="show-result">${game.round === 3 ? 'See final result' : 'Result & next hand'}</button></div>`,
    );
  }
  if (game.online && game.status === 'ready') {
    setHTML(
      'table-overlay',
      `<div class="start-card"><span class="eyebrow">ONLINE MATCH</span><h2>East ${game.round + 1} starts soon</h2><p>Everyone draws from the same wall.<br>5 seconds each to draw and to discard.</p></div>`,
    );
  }
  renderHand();
  renderReservations();
  renderClock();
  renderOnline();
  if (game.result && shownResult !== game.result) {
    shownResult = game.result;
    openResult();
  }
  updateResultLock();
  updateTileLoading();
}

let inTick = false;
let renderQueued = false;
function onGameChange(e) {
  if (['start', 'resume'].includes(e.event)) lastTick = performance.now();
  if (e.event === 'ready' || (e.event === 'timeout' && game.lastTimeout?.player === 0)) stopSelecting();
  if (!game.result && $id('result-dialog').open) {
    $id('result-dialog').close();
    shownResult = null;
  }
  if (inTick) renderQueued = true;
  else render();
  if (e.message) $id('recent-event').textContent = e.message;
  if (e.event === 'call' || e.event === 'riichi' || (e.event === 'timeout' && game.lastTimeout?.player === 0)) toast(e.message);
  if (['discard', 'draw', 'call', 'win', 'riichi'].includes(e.event)) beep(e.event);
}
local.onChange = onGameChange;

document.addEventListener('click', async (e) => {
  const el = e.target.closest('[data-action]');
  if (!el || el.disabled) return;
  const action = el.dataset.action;
  if (['inspect', 'next-round', 'new-match', 'online-leave', 'confirm-leave', 'game-back'].includes(action) && resultLocked()) {
    extendMash();
    return;
  }
  try {
    if (action.startsWith('online-')) return await onlineAction(action);
    switch (action) {
      case 'home-cpu':
        showPage('game');
        return render();
      case 'home-online':
        return openOnline('random');
      case 'home-room':
        return openOnline('room');
      case 'game-back':
        return goBack();
      case 'settings':
        return openSettings();
      case 'close-settings':
        return $id('settings-dialog').close();
      case 'close-leave':
        return $id('leave-dialog').close();
      case 'confirm-leave':
        el.disabled = true;
        try {
          await leaveRoom('home');
        } finally {
          el.disabled = false;
        }
        return;
      case 'retry-tiles':
        return tiles.load();
      case 'start':
        if (tiles.state.status === 'ready') game.start();
        return;
      case 'draw':
        if (!selecting) await game.draw(0);
        return;
      case 'discard':
        if (!selecting) await game.discard(0, el.dataset.tile);
        return;
      case 'riichi':
        if (!selecting) game.armRiichi();
        return;
      case 'start-reservation':
        return toggleSelecting();
      case 'select-reservation-tile':
        return await pickTile(Number(el.dataset.index), el.dataset.hand);
      case 'pause':
        return game.status === 'paused' ? game.resume() : game.pause();
      case 'reserve':
        return await game.toggleReservation(el.dataset.key);
      case 'clear-reservations':
        return await game.clearReservations();
      case 'rules':
        return openRules();
      case 'close-rules':
        return closeRules();
      case 'sound':
        sound = !sound;
        el.textContent = sound ? 'Sound on' : 'Sound off';
        el.setAttribute('aria-label', sound ? 'Turn sound off' : 'Turn sound on');
        if (sound) beep('draw');
        return;
      case 'inspect':
        inspected = game.result;
        $id('result-dialog').close();
        $id('table-overlay').hidden = true;
        return;
      case 'show-result':
        return $id('result-dialog').showModal();
      case 'next-round':
        $id('result-dialog').close();
        return game.nextRound();
      case 'new-match':
        $id('result-dialog').close();
        shownResult = null;
        return game.newMatch();
    }
  } catch (err) {
    if (game.online || action.startsWith('online-')) {
      toast(err.message);
      $id('online-error').textContent = err.message;
    } else {
      game.pause();
      toast('Something went wrong, so the game was paused. Resume to continue.');
      console.error(err);
    }
  }
});

$id('speed').addEventListener('change', (e) => void changeSpeed(e.target.value));
$id('lobby-speed').addEventListener('change', (e) => void changeSpeed(e.target.value, true));
let labels = true;
try {
  labels = localStorage.getItem('speed-mahjong-labels') !== 'off';
} catch {}
function applyLabels() {
  document.documentElement.classList.toggle('tile-labels', labels);
  $id('tile-labels').checked = labels;
}
$id('tile-labels').addEventListener('change', (e) => {
  labels = e.target.checked;
  try {
    localStorage.setItem('speed-mahjong-labels', labels ? 'on' : 'off');
  } catch {}
  applyLabels();
});
applyLabels();

$id('auto-draw').addEventListener('change', async (e) => {
  try {
    await game.setAutoDraw(e.target.checked);
  } catch (err) {
    toast(err.message);
    e.target.checked = game.autoDraw;
  }
});

document.addEventListener('keydown', async (e) => {
  if (e.code === 'Escape' && selecting) {
    stopSelecting();
    render();
    return;
  }
  if (page !== 'game' || e.code !== 'Space' || selecting || e.repeat || document.querySelector('dialog[open]')) return;
  if (['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;
  e.preventDefault();
  try {
    await game.draw(0);
  } catch (err) {
    toast(err.message);
  }
});

document.addEventListener('visibilitychange', () => {
  if (!game.online && document.hidden && game.status === 'playing') {
    game.pause();
    $id('recent-event').textContent = 'You left the page, so everyone was paused.';
  }
});

let lastTick = performance.now();
setInterval(() => {
  const now = performance.now();
  const dt = Math.max(0, now - lastTick);
  lastTick = now;
  try {
    inTick = true;
    try {
      game.tick(dt, { holdAutoDraw: selecting });
    } finally {
      inTick = false;
    }
    if (renderQueued) {
      renderQueued = false;
      render();
    } else {
      renderClock();
      updateResultLock();
      if (game.online || net.room?.status === 'waiting') renderOnline();
    }
  } catch (err) {
    game.pause();
    toast('The game was paused.');
    console.error(err);
  }
}, 100);

// ---------- online ----------
let connection = 'idle';
let leaving = false;
let onlineGame = null;
let speedChange = null;
const net = new OnlineClient({
  onRoom,
  onStatus: (s) => {
    connection = s;
    renderOnline();
  },
});

async function changeSpeed(speed, fromLobby = false) {
  if (!fromLobby && !game.online) {
    game.setSpeed(speed);
    renderOnline();
    return;
  }
  const room = net.room;
  if (!room?.host || speedChange || (game.online && game.pending)) {
    renderOnline();
    return;
  }
  speedChange = { roomId: room.id, value: speed };
  $id('speed-error').textContent = '';
  $id('online-error').textContent = '';
  renderOnline();
  try {
    if (game.online) await game.setSpeed(speed);
    else await net.action({ action: 'cpu-speed', speed });
  } catch (err) {
    if (net.room?.id === room.id) {
      $id('speed-error').textContent = err.message;
      $id('online-error').textContent = err.message;
    }
  } finally {
    speedChange = null;
    renderOnline();
  }
}

function renderSpeed(room) {
  const changing = speedChange?.roomId === room?.id && !!speedChange;
  const editable = !!room?.host && ['waiting', 'playing'].includes(room.status) && !leaving && !changing && !(game.online && game.pending);
  const labels = { slow: 'Relaxed', normal: 'Normal', fast: game.online ? 'Quick' : 'Fast' };
  for (const opt of $id('speed').options) {
    const text = labels[opt.value] + (game.online ? ` (~${ONLINE_SPEED[opt.value] / 1000}s per tile)` : '');
    if (opt.textContent !== text) opt.textContent = text;
  }
  $id('speed').value = game.online && changing ? speedChange.value : game.speed;
  $id('speed').disabled = game.online && !editable;
  $id('speed-description').hidden = !game.online;
  $id('speed-description').textContent = game.online
    ? `${changing ? 'Changing speed…' : room?.host ? 'Shared by everyone. Applies to CPUs not in riichi.' : 'Only the host can change this. It is shared by everyone.'} After riichi, everyone moves at 1 tile per second.`
    : '';
  $id('lobby-speed').value = changing ? speedChange.value : room?.cpuSpeed || 'normal';
  $id('lobby-speed').disabled = !editable;
  $id('lobby-speed-description').textContent = changing
    ? 'Changing speed…'
    : room?.host
      ? 'Shared by everyone. Applies to CPUs in empty or abandoned seats.'
      : 'Only the host can change this. It is shared by everyone.';
}

function renderOnline() {
  const room = net.room;
  renderSpeed(room);
  $id('friend-options').hidden = onlineMode !== 'room';
  $id('random-options').hidden = onlineMode !== 'random';
  $id('online-title').textContent = room?.kind === 'private' || (!room && onlineMode === 'room') ? 'Room match' : 'Online';
  const fillIn = room?.matchAt == null ? null : Math.max(0, Math.ceil((room.matchAt - room.serverNow - (performance.now() - net.roomReceivedAt)) / 1000));
  $id('mode-label').textContent = game.online ? `${room?.kind === 'private' ? 'Room match' : 'Online match'} · East round` : 'Solo vs CPUs · East round';
  $id('opponent-label').textContent = game.online ? 'Online' : '3 CPUs';
  $id('online-strip').hidden = !room;
  $id('connection-label').textContent =
    connection === 'reconnecting'
      ? 'Reconnecting · the clock keeps running'
      : game.online && game.status === 'ready'
        ? `Starting in ${Math.max(0, Math.ceil((game.startAt - game.now()) / 1000))}s`
        : game.online && game.status === 'ended' && game.nextAt
          ? `Next hand in ${Math.max(0, Math.ceil((game.nextAt - game.now()) / 1000))}s`
          : room?.status === 'waiting'
            ? fillIn === null
              ? 'Waiting for players'
              : `CPUs join in ${fillIn}s`
            : 'Connected';
  $id('online-strip').classList.toggle('connection-error', connection === 'reconnecting');
  $id('online-room-label').textContent = room?.kind === 'private' ? `ROOM ${room.code}` : 'ONLINE MATCH';
  $id('online-settings').hidden = !room;
  $id('online-choices').hidden = !!room;
  $id('online-lobby').hidden = !room;
  $id('online-close').hidden = false;
  $id('online-close').disabled = leaving;
  if (!room) return;
  const waiting = room.status === 'waiting';
  $id('match-countdown').hidden = !waiting || fillIn === null;
  $id('match-countdown').textContent = fillIn > 0 ? `Starting in ${fillIn}s; CPUs fill any empty seats` : 'Filling empty seats with CPUs…';
  $id('lobby-title').textContent = waiting ? (room.kind === 'private' ? 'Invite your friends' : 'Looking for opponents') : room.status === 'finished' ? 'Match over' : 'Match in progress';
  $id('room-code-block').hidden = room.kind !== 'private';
  $id('room-code').textContent = room.code;
  setHTML(
    'lobby-seats',
    room.members
      .map(
        (m, i) =>
          `<div class="lobby-seat ${m.name || !waiting ? 'occupied' : ''}"><span class="seat">${WINDS[i]}</span><div><strong>${m.name ? esc(m.name) : waiting ? 'Open seat' : `CPU ${String(i + 1).padStart(2, '0')}`}</strong><small>${m.you ? 'You' : m.host ? 'Host' : m.left ? 'CPU took over' : m.name ? (m.connected ? 'Here' : 'Reconnecting') : waiting ? (room.kind === 'private' ? 'A CPU joins at the start' : 'Looking for a player') : 'CPU playing'}</small></div><span class="seat-dot ${m.name && !m.left && m.connected ? 'connected' : ''}"></span></div>`,
      )
      .join(''),
  );
  const players = room.members.filter((m) => m.name && !m.left).length;
  $id('lobby-description').textContent = waiting
    ? room.kind === 'private'
      ? 'Share the room code or invite link. Start with 2 to 4 players; CPUs take the empty seats.'
      : `${players}/4 players · waits up to 30 seconds, starts right away at 4. You can cancel while waiting.`
    : "You can't pause an online match. If you leave, a CPU takes over your seat.";
  $id('room-start').hidden = !waiting || !room.host || room.kind !== 'private';
  $id('room-start').disabled = players < 2 || leaving;
  $id('online-back').hidden = waiting;
  $id('online-leave').textContent = waiting ? 'Cancel' : 'Leave match';
}

function onRoom(room) {
  const before = game;
  if (room?.game) {
    if (!onlineGame || onlineGame.roomId !== room.id) {
      onlineGame = new OnlineGame((body) => net.action(body));
      onlineGame.roomId = room.id;
      onlineGame.onChange = onGameChange;
      handKey = '';
      shownResult = null;
    }
    if (game !== onlineGame) {
      if (local.status === 'playing') local.pause();
      stopSelecting();
      game = onlineGame;
    }
    if (before !== onlineGame) {
      $id('online-dialog').close();
      $id('settings-dialog').close();
      reopenSettings = false;
      $id('rules-dialog').close();
      pausedByDialog = false;
      showPage('game');
    }
    onlineGame.update(room);
    $id('auto-draw').checked = onlineGame.autoDraw;
  } else if (room) {
    onlineMode = room.kind === 'private' ? 'room' : 'random';
    if (local.status === 'playing') local.pause();
    showPage('home');
    if (!$id('online-dialog').open) $id('online-dialog').showModal();
  } else if (game.online) {
    stopSelecting();
    game = local;
    onlineGame = null;
    handKey = '';
    shownResult = null;
    $id('result-dialog').close();
    $id('leave-dialog').close();
    showPage('home');
    render();
  }
  if (before !== game) handKey = '';
  renderOnline();
  net.schedule();
}

async function leaveRoom(then) {
  if (leaving) return;
  leaving = true;
  renderOnline();
  try {
    await net.leave();
    $id('result-dialog').close();
    $id('leave-dialog').close();
    showPage('home');
    if (then === 'menu') {
      if (!$id('online-dialog').open) $id('online-dialog').showModal();
    } else $id('online-dialog').close();
  } finally {
    leaving = false;
    renderOnline();
  }
}

async function onlineAction(action) {
  if (action === 'online-open') return openOnline(net.room?.kind === 'private' ? 'room' : 'random');
  if (action === 'online-close') {
    if (leaving) return;
    if (net.room?.status === 'waiting') return leaveRoom('menu');
    return $id('online-dialog').close();
  }
  if (action === 'online-back') return $id('online-dialog').close();
  if (action === 'online-copy' || action === 'online-link') {
    if (!net.room) return;
    const text = action === 'online-copy' ? net.room.code : `${location.origin}${location.pathname}?room=${net.room.code}`;
    try {
      await navigator.clipboard.writeText(text);
      toast(action === 'online-copy' ? 'Room code copied' : 'Invite link copied');
    } catch {
      $id('online-error').textContent = `Couldn't copy. Share the room code ${net.room.code} instead.`;
    }
    return;
  }
  if (action === 'online-retry') return net.retry();
  if (action === 'online-leave') {
    if (net.room?.status === 'waiting') return leaveRoom('menu');
    if (game.online && !(game.status === 'ended' && game.round === 3)) {
      $id('online-dialog').close();
      return $id('leave-dialog').showModal();
    }
    return leaveRoom('home');
  }
  if (leaving) return;
  leaving = true;
  $id('online-error').textContent = '';
  $id('online-choices').classList.add('busy');
  renderOnline();
  try {
    const name = $id('online-name').value;
    try {
      localStorage.setItem('speed-mahjong-name', name);
    } catch {}
    if (action === 'online-create') await net.lobby('create', name);
    else if (action === 'online-join') await net.lobby('join', name, $id('join-code').value);
    else if (action === 'online-match') await net.lobby('match', name);
    else if (action === 'online-start') await net.request('/api/rooms/start', {});
  } finally {
    leaving = false;
    $id('online-choices').classList.remove('busy');
    renderOnline();
  }
}

$id('online-dialog').addEventListener('cancel', (e) => {
  e.preventDefault();
  onlineAction('online-close').catch((err) => {
    $id('online-error').textContent = err.message;
  });
});
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && net.room) net.retry();
});

// ---------- boot ----------
try {
  $id('online-name').value = localStorage.getItem('speed-mahjong-name') || '';
} catch {}
net
  .init()
  .then(({ name }) => {
    if (!$id('online-name').value && name !== 'Guest') $id('online-name').value = name;
  })
  .catch(() => {});
const invite = new URLSearchParams(location.search).get('room');
if (invite && /^[A-Z2-9]{6}$/i.test(invite)) {
  $id('join-code').value = invite.toUpperCase();
  openOnline('room');
}
setupDisplay({ announce: toast });
showPage('home');
render();
tiles.load();
